import { useEffect, useRef, useState } from 'react';
import { m, AnimatePresence } from 'motion/react';
import { X, PhoneCall, Check } from 'lucide-react';
import { submitLead } from '../../lib/leads';
import { trackLeadSubmit, trackCtaClick, trackWhatsAppClick } from '../../lib/analytics';
import { whatsappUrl, WhatsAppIcon } from './WhatsAppFAB';

// FloorPlans.tsx'teki TABS ile aynı liste — formdaki seçenekler kat planlarıyla
// birebir örtüşsün diye. Orası değişirse burası da değişmeli.
const PLANS = ['2+1', '3+1', '4+1', 'Dubleks', 'Bahçe Loft Dubleks'] as const;

// Panel 300px + 24px kenar boşluğu kaplar. Hero paragrafı max-w-2xl (672px) ve
// ortalı olduğu için dar masaüstünde panelin sol kenarı metnin üstüne biner.
// Bu yüzden geniş ekranda açık, dar ekranda kapalı (buton) başlar.
const WIDE_SCREEN = '(min-width: 1440px)';

// KVKK aydınlatma metni. Sayfa public/kvkk.html içinde — canlıya çıkmadan önce
// şirket bilgileriyle doldurulmalı.
const KVKK_URL = '/kvkk.html';

const ATTRIBUTION_KEYS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid',
] as const;

/** Reklam parametrelerini URL'den bir kez okur; lead ile birlikte gönderilir. */
function readAttribution(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const out: Record<string, string> = {};
  for (const key of ATTRIBUTION_KEYS) {
    const value = params.get(key);
    if (value) out[key] = value;
  }
  return out;
}

const EMPTY_FORM = { firstName: '', lastName: '', phone: '', plan: '' };

export function LeadWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [kvkkOk, setKvkkOk] = useState(false);
  const [marketingOk, setMarketingOk] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attribution] = useState(readAttribution);

  // Kullanıcı paneli eliyle açıp kapattıysa ekran boyutu artık karar vermesin.
  const touched = useRef(false);

  const toggle = (next: boolean) => {
    touched.current = true;
    setIsOpen(next);
    if (next) trackCtaClick('Fiyat Listesi Al', 'lead_widget');
  };

  useEffect(() => {
    const mq = window.matchMedia(WIDE_SCREEN);
    const apply = () => {
      if (!touched.current) setIsOpen(mq.matches);
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  // Panel açıkken Esc kapatsın.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        touched.current = true;
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  const change = (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSending(true);
    try {
      await submitLead({
        formName: 'lead_widget',
        firstName: form.firstName,
        lastName: form.lastName,
        phone: form.phone,
        plan: form.plan,
        marketingConsent: marketingOk,
        attribution,
      });
      // Dönüşüm yalnızca gönderim GERÇEKTEN başarılı olunca sayılır.
      trackLeadSubmit({ form_name: 'lead_widget', lead_plan: form.plan });
      setIsSent(true);
      setForm(EMPTY_FORM);
      setKvkkOk(false);
      setMarketingOk(false);
    } catch {
      setError('Gönderilemedi, tekrar deneyin.');
    } finally {
      setIsSending(false);
    }
  };

  // Aydınlık buzlu cam: zemin %80 saydam + güçlü blur, yani panelin rengini
  // arkasındaki hero'nun kendi sıcak tonları belirliyor — koyu blok gibi
  // durmuyor. Vurgu rengi slate değil stone: Contact ve Testimonials
  // başlıklarındaki mevcut sıcak aksanın aynısı.
  const inputClass =
    'w-full h-10 rounded-lg px-3 text-sm outline-none transition-colors ' +
    'border border-slate-900/10 bg-white/70 text-slate-900 placeholder:text-slate-600 ' +
    'hover:border-slate-900/20 focus:border-stone-500 focus:bg-white ' +
    'dark:border-white/15 dark:bg-white/10 dark:text-white dark:placeholder:text-white/55 ' +
    'dark:hover:border-white/25 dark:focus:border-white/40 dark:focus:bg-white/15';

  const labelClass =
    'block mb-1 text-[11px] font-medium tracking-wide text-slate-600 dark:text-white/55';

  return (
    <>
      {/* Kapalı durum — ekranın altında yapışkan şerit (1440px altı).
          Sürekli görünür ama ekranın yalnızca ~%10'unu kaplıyor: Google
          "makul yer kaplayan ve kapatılabilir" şeritleri intrusive interstitial
          saymıyor. Formu sürekli açık bırakmak iPhone SE'de ekranın %85'ini
          kapatıyordu — hem organik sıralamayı hem Ads kalite puanını düşürür.
          WhatsApp da şeridin içinde, böylece FAB ile çakışma kalmıyor. */}
      {/* Masaüstünde panel kapatılınca geri açacak düğme. Şerit 1440px üstünde
          gizli olduğu için bu olmadan panel bir daha açılamıyordu.
          WhatsApp balonu bottom-6 + 64px yer kaplıyor; düğme 112px'ten başlayıp
          onun üstünde duruyor. */}
      <AnimatePresence>
        {!isOpen && (
          <m.button
            type="button"
            onClick={() => toggle(true)}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-28 right-6 z-50 hidden min-[1440px]:inline-flex items-center gap-2
                       px-4 py-2.5 rounded-full text-sm tracking-wide transition-colors
                       bg-stone-800 text-white hover:bg-stone-700 shadow-lg shadow-slate-900/20
                       dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
            aria-label="Bilgi formunu yeniden aç"
          >
            <PhoneCall size={15} aria-hidden="true" />
            Fiyat Listesi Al
          </m.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {!isOpen && (
          <m.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-x-0 bottom-0 z-50 min-[1440px]:hidden
                       border-t border-slate-900/10 bg-white/90 backdrop-blur-md
                       dark:border-white/10 dark:bg-slate-900/90"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            <div className="flex items-center gap-2 px-3 py-2.5">
              <button
                type="button"
                onClick={() => toggle(true)}
                className="flex-1 h-12 inline-flex items-center justify-center gap-2 rounded-lg
                           text-sm font-medium tracking-wide transition-colors
                           bg-stone-800 text-white hover:bg-stone-700
                           dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
              >
                <PhoneCall size={16} aria-hidden="true" />
                Fiyat Listesi Al
              </button>
              <a
                href={whatsappUrl()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackWhatsAppClick('sticky_bar')}
                className="w-12 h-12 shrink-0 inline-flex items-center justify-center rounded-lg
                           text-white transition-opacity hover:opacity-90"
                style={{ background: 'linear-gradient(145deg, #2edb6e, #1aad50)' }}
                aria-label="WhatsApp ile iletişime geç"
              >
                <WhatsAppIcon className="w-6 h-6" />
              </a>
            </div>
          </m.div>
        )}
      </AnimatePresence>

      {/* Mobil/tablette panel açıkken arkaya örtü — masaüstünde yok */}
      <AnimatePresence>
        {isOpen && (
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => toggle(false)}
            className="fixed inset-0 z-[55] bg-slate-950/45 min-[1440px]:hidden"
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <m.aside
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            aria-label="Rain Nova bilgi talep formu"
            // Masaüstünde alta yaslı: WhatsApp butonunun üst kenarı ekranın
            // altından 88px yukarıda, panel 112px'ten başlıyor — hiçbir ekran
            // yüksekliğinde çakışmaz. Dikey ortalama kısa ekranlarda butonun
            // üstüne biniyordu.
            className="fixed z-[60] overflow-y-auto
                       inset-x-3 bottom-3 mx-auto max-w-sm max-h-[90vh] p-4
                       [@media(max-height:480px)_and_(min-width:700px)]:max-w-2xl
                       min-[1440px]:inset-x-auto min-[1440px]:right-6 min-[1440px]:bottom-28
                       min-[1440px]:w-[300px] min-[1440px]:max-h-[calc(100vh-10rem)] min-[1440px]:p-5
                       rounded-xl backdrop-blur-2xl
                       border border-white/60 bg-white/80 shadow-xl shadow-slate-900/15
                       dark:border-white/10 dark:bg-slate-900/80 dark:shadow-slate-950/40"
          >
            <button
              type="button"
              onClick={() => toggle(false)}
              className="absolute top-2.5 right-2.5 p-1.5 rounded-lg transition-colors
                         text-slate-400 hover:text-slate-900 hover:bg-slate-900/5
                         dark:text-white/40 dark:hover:text-white dark:hover:bg-white/10"
              aria-label="Formu kapat"
            >
              <X size={16} aria-hidden="true" />
            </button>

            {isSent ? (
              <div className="py-6 text-center">
                <span className="inline-flex items-center justify-center w-10 h-10 mb-3 rounded-full bg-emerald-500/15">
                  <Check size={20} className="text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
                </span>
                <h3 className="mb-1.5 text-base font-semibold text-slate-900 dark:text-white">
                  Talebiniz alındı
                </h3>
                <p className="text-xs text-slate-600 dark:text-white/60">
                  Satış ekibimiz en kısa sürede sizi arayacak.
                </p>
                <button
                  type="button"
                  onClick={() => setIsSent(false)}
                  className="mt-4 text-xs tracking-wide underline underline-offset-2 transition-colors
                             text-slate-500 hover:text-slate-900
                             dark:text-white/60 dark:hover:text-white"
                >
                  Yeni talep oluştur
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <h3 className="mb-0.5 pr-6 text-base font-semibold tracking-tight text-slate-900 dark:text-white">
                  Sizi arayalım
                </h3>
                <p className="mb-4 text-[11px] text-slate-600 dark:text-white/60">
                  Fiyat listesi ve kat planları için bilgilerinizi bırakın.
                </p>

                {/* Ad + soyad tek satırda — panel yüksekliğini bir alan kadar kısaltıyor.
                    Yatay moddaki telefonda (ekran yüksekliği ~390px) form dikey
                    dizilince sığmıyor; o ekranlarda telefon da aynı satıra geçip
                    üç sütun oluyor. Sınıflar tam yazılmalı — Tailwind kaynak
                    taramasında birleştirilmiş sınıf adlarını göremez. */}
                <div className="grid grid-cols-2 gap-2 mb-2.5 [@media(max-height:480px)_and_(min-width:700px)]:grid-cols-3">
                  <div>
                    <label className={labelClass} htmlFor="lwFirstName">Adınız</label>
                    <input
                      id="lwFirstName" name="firstName" type="text" required
                      autoComplete="given-name" value={form.firstName}
                      onChange={change} className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass} htmlFor="lwLastName">Soyadınız</label>
                    <input
                      id="lwLastName" name="lastName" type="text" required
                      autoComplete="family-name" value={form.lastName}
                      onChange={change} className={inputClass}
                    />
                  </div>
                  <div className="col-span-2 [@media(max-height:480px)_and_(min-width:700px)]:col-span-1">
                    <label className={labelClass} htmlFor="lwPhone">Cep Telefonu</label>
                    <input
                      id="lwPhone" name="phone" type="tel" required
                      inputMode="tel" autoComplete="tel" placeholder="05xx xxx xx xx"
                      pattern="[0-9+\s()-]{10,20}" value={form.phone}
                      onChange={change} className={inputClass}
                    />
                  </div>
                </div>

                <span className={labelClass}>Size uygun planı seçin</span>
                <div
                  className="grid grid-cols-2 gap-1.5 mb-3 [@media(max-height:480px)_and_(min-width:700px)]:grid-cols-5"
                  role="radiogroup" aria-label="Daire tipi"
                >
                  {PLANS.map((plan, i) => (
                    <label
                      key={plan}
                      className={
                        i === PLANS.length - 1
                          ? 'col-span-2 [@media(max-height:480px)_and_(min-width:700px)]:col-span-1'
                          : undefined
                      }
                    >
                      <input
                        type="radio" name="plan" value={plan} required
                        checked={form.plan === plan} onChange={change}
                        className="peer sr-only"
                      />
                      {/* Radyo görsel olarak gizli; klavye odağı görünsün diye
                          halka peer-focus-visible ile etiketin üstünde gösteriliyor. */}
                      <span
                        className="flex items-center justify-center h-8 rounded-lg cursor-pointer text-xs font-medium
                                   transition-colors border border-slate-900/10 bg-white/60 text-slate-700
                                   hover:border-stone-400 hover:text-slate-900
                                   peer-checked:bg-stone-700 peer-checked:text-white peer-checked:border-stone-700
                                   peer-focus-visible:ring-2 peer-focus-visible:ring-stone-500/60
                                   peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-white
                                   dark:border-white/15 dark:bg-white/[0.06] dark:text-white/70
                                   dark:hover:border-white/30 dark:hover:text-white
                                   dark:peer-checked:bg-white dark:peer-checked:text-slate-900 dark:peer-checked:border-white
                                   dark:peer-focus-visible:ring-white/60 dark:peer-focus-visible:ring-offset-slate-900"
                      >
                        {plan}
                      </span>
                    </label>
                  ))}
                </div>

                <label className="flex items-center gap-2 mb-2 text-[11px] leading-snug cursor-pointer text-slate-600 dark:text-white/60">
                  <input
                    type="checkbox" name="kvkk" required
                    checked={kvkkOk} onChange={(e) => setKvkkOk(e.target.checked)}
                    className="w-6 h-6 shrink-0 accent-stone-700 dark:accent-white"
                  />
                  <span>
                    <a
                      href={KVKK_URL} target="_blank" rel="noopener noreferrer"
                      className="inline-block py-[5px] underline underline-offset-2 text-slate-800 hover:text-slate-950
                                 dark:text-white/80 dark:hover:text-white"
                    >
                      KVKK Aydınlatma Metni'ni
                    </a>{' '}
                    okudum.
                  </span>
                </label>

                {/* Ticari ileti izni AYRI ve ZORUNLU DEĞİL. KVKK açık rızası özgür
                    iradeyle ve ayrı ayrı alınır; aydınlatma onayıyla tek zorunlu
                    kutuda birleştirilemez. Kutu işaretlenmeden SMS/arama kampanyası
                    yapılamaz — ayrıca İYS kaydı gerekir. */}
                <label className="flex items-center gap-2 mb-2 text-[11px] leading-snug cursor-pointer text-slate-600 dark:text-white/60">
                  <input
                    type="checkbox" name="marketing"
                    checked={marketingOk} onChange={(e) => setMarketingOk(e.target.checked)}
                    className="w-6 h-6 shrink-0 accent-stone-700 dark:accent-white"
                  />
                  <span>Kampanya bildirimleri almak istiyorum.</span>
                </label>

                <button
                  type="submit"
                  disabled={isSending}
                  className="w-full h-11 rounded-lg text-sm font-medium tracking-wide transition-all duration-300
                             bg-stone-800 text-white hover:bg-stone-700
                             dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100
                             disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSending ? 'Gönderiliyor...' : 'Hemen Başvur'}
                </button>

                <p className="mt-1.5 text-[11px] text-center text-red-800 dark:text-red-300 empty:hidden" aria-live="polite">
                  {error}
                </p>
              </form>
            )}
          </m.aside>
        )}
      </AnimatePresence>
    </>
  );
}
